/// <amd-module name="Repos/Views/PullRequestListingPageActions/PullRequestListingPageActionsService" />
import * as React from "react";
import { PullRequestStatus } from "Repos/Common/Contracts/Git";
import { IVersionControlUserPreferences } from "Repos/Common/Contracts/VersionControlViewModel";
import { IVCViewModelDataProviderService } from "Repos/Common/VCViewModelDataProviderService";
import { IPullRequestQuery } from "Repos/PullRequestList/Contracts";
import { IPullRequestDataProviderService } from "Repos/PullRequestList/PullRequestDataProviderService";
import * as Resources from "Repos/Views/PullRequestListingPageActions/Resources";
import { ITfsTeamService } from "TFS/Services/TfsTeamService";
import { ObservableArray, ObservableValue } from "VSS/Core/Observable";
import { format } from "VSS/Core/Util/String";
import { PeoplePickerProvider } from "VSS/Features/Identities/IdentityPickerProvider";
import { showMessageDialog } from "VSS/Features/PlatformUI/MessageDialog";
import { IVssService, Services, VssService } from "VSS/Platform/Context";
import { IVssLayoutManager } from "VSS/Platform/Layout";
import { Observer } from "VSSUI/Observer";
import { CustomizeQueriesPanel } from "./Components/CustomizeQueriesPanel";
import { CustomizeQueryPanel, ICustomizeQueryPanelProps } from "./Components/CustomizeQueryPanel";

export interface IPullRequestListingPageActionsService extends IVssService {
    showCustomizePanel(query: IPullRequestQuery, onDismiss: () => void): void;
    showCustomizeQueriesPanel(): void;
}

class PullRequestListingPageActionsService extends VssService implements IPullRequestListingPageActionsService {
    private isChanged: boolean = false;

    public async showCustomizeQueriesPanel(): Promise<void> {
        const prDataProviderService = this.pageContext.getService<IPullRequestDataProviderService>("IPullRequestDataProviderService");

        const errorMessage = new ObservableValue("");
        const queries = new ObservableArray(await prDataProviderService.getPullRequestQueries());
        const showOverlay = new ObservableValue(false);
        const selectedQueryProps = new ObservableValue<ICustomizeQueryPanelProps | undefined>(undefined);

        this.pageContext.getService<IVssLayoutManager>("IVssLayoutManager").renderCallout((onDismiss: () => void) => {
            return (
                <Observer selectedQueryPropsValue={selectedQueryProps}>
                    {({ selectedQueryPropsValue }: { selectedQueryPropsValue?: ICustomizeQueryPanelProps }) => {
                        return (
                            <>
                                {selectedQueryPropsValue && <CustomizeQueryPanel {...selectedQueryPropsValue} />}
                                <CustomizeQueriesPanel
                                    errorMessage={errorMessage}
                                    onDeleteQuery={query => {
                                        errorMessage.value = "";
                                        showMessageDialog(this.pageContext, {
                                            message: format(Resources.ConfirmRemoveQueryFormat, query.title),
                                            onClose: async result => {
                                                if (result) {
                                                    showOverlay.value = true;
                                                    try {
                                                        const newQueries = queries.value.filter(q => q.id !== query.id);
                                                        await this.saveQueries(newQueries, true);
                                                        queries.value = newQueries;
                                                    } catch (ex) {
                                                        errorMessage.value = ex.message;
                                                    }
                                                    showOverlay.value = false;
                                                }
                                            },
                                            okButtonProps: {
                                                danger: true,
                                                primary: false,
                                                text: Resources.RemoveSection
                                            }
                                        });
                                    }}
                                    onDismiss={() => {
                                        this.onDismiss(onDismiss);
                                    }}
                                    onEditQuery={async query => {
                                        errorMessage.value = "";
                                        showOverlay.value = true;
                                        selectedQueryProps.value = await this.getCustomizeQueryPanelProps(
                                            query,
                                            async newQuery => {
                                                const newQueries = queries.value.map(q => (q.id === newQuery.id ? newQuery : q));
                                                await this.saveQueries(newQueries, true);
                                                queries.value = newQueries;
                                            },
                                            () => {
                                                selectedQueryProps.value = undefined;
                                                showOverlay.value = false;
                                            }
                                        );
                                    }}
                                    onMoveQuery={async (query, up) => {
                                        errorMessage.value = "";
                                        showOverlay.value = true;
                                        try {
                                            const queryIndex = queries.value.findIndex(q => q.id === query.id);
                                            if (queryIndex >= 0) {
                                                const newQueries = [...queries.value];
                                                if (up && queryIndex > 0) {
                                                    newQueries[queryIndex] = newQueries[queryIndex - 1];
                                                    newQueries[queryIndex - 1] = query;
                                                } else if (!up && queryIndex < queries.length - 1) {
                                                    newQueries[queryIndex] = newQueries[queryIndex + 1];
                                                    newQueries[queryIndex + 1] = query;
                                                }

                                                await this.saveQueries(newQueries, true);
                                                queries.value = newQueries;
                                            }
                                        } catch (ex) {
                                            errorMessage.value = ex.message;
                                        }
                                        showOverlay.value = false;
                                    }}
                                    onNewQuery={async () => {
                                        let queryNumber = 1;
                                        let queryId = "";
                                        while (true) {
                                            queryId = `customQuery${queryNumber}`;
                                            if (queries.value.find(q => q.id === queryId)) {
                                                queryNumber++;
                                            } else {
                                                break;
                                            }
                                        }
                                        const query: IPullRequestQuery = {
                                            id: queryId,
                                            groupByVote: true,
                                            status: PullRequestStatus.Active,
                                            title: Resources.NewSectionTitle
                                        };
                                        errorMessage.value = "";
                                        showOverlay.value = true;
                                        selectedQueryProps.value = await this.getCustomizeQueryPanelProps(
                                            query,
                                            async newQuery => {
                                                const newQueries = [...queries.value, newQuery];
                                                await this.saveQueries(newQueries, true);
                                                queries.value = newQueries;
                                            },
                                            () => {
                                                selectedQueryProps.value = undefined;
                                                showOverlay.value = false;
                                            }
                                        );
                                    }}
                                    onResetQueries={() => {
                                        errorMessage.value = "";
                                        showMessageDialog(this.pageContext, {
                                            message: Resources.ConfirmResetQueries,
                                            onClose: async result => {
                                                if (result) {
                                                    showOverlay.value = true;
                                                    try {
                                                        await this.saveQueries([], true);
                                                        queries.value = await prDataProviderService.getPullRequestQueries(true);
                                                    } catch (ex) {
                                                        errorMessage.value = ex.message;
                                                    }
                                                    showOverlay.value = false;
                                                }
                                            },
                                            okButtonProps: {
                                                danger: true,
                                                primary: false,
                                                text: Resources.Reset
                                            },
                                            title: Resources.ResetSections
                                        });
                                    }}
                                    queries={queries}
                                    showOverlay={showOverlay}
                                />
                            </>
                        );
                    }}
                </Observer>
            );
        });
    }

    public async showCustomizePanel(query: IPullRequestQuery, onDismiss: () => void): Promise<void> {
        let panelDismiss = () => {};
        const props = await this.getCustomizeQueryPanelProps(
            query,
            async newQuery => {
                const prDataService = this.pageContext.getService<IPullRequestDataProviderService>("IPullRequestDataProviderService");
                const queries = await prDataService.getPullRequestQueries();

                const newQueries: IPullRequestQuery[] = [];
                for (const query of queries) {
                    if (query.id === newQuery.id) {
                        newQueries.push(newQuery);
                    } else {
                        newQueries.push(query);
                    }
                }

                return this.saveQueries(newQueries, true);
            },
            () => {
                this.onDismiss();
                panelDismiss();
                onDismiss();
            }
        );

        this.pageContext.getService<IVssLayoutManager>("IVssLayoutManager").renderCallout((onDismiss: () => void) => {
            panelDismiss = onDismiss;
            return <CustomizeQueryPanel {...props} />;
        });
    }

    public async getCustomizeQueryPanelProps(
        query: IPullRequestQuery,
        saveQuery: (query: IPullRequestQuery) => Promise<void>,
        onDismiss: (changed: boolean) => void
    ): Promise<ICustomizeQueryPanelProps> {
        const usersOnlyProvider = new PeoplePickerProvider(this.pageContext, {
            identityTypes: ["user"],
            options: {
                MinResults: 5,
                MaxResults: 20
            },
            operationScopes: ["ims"]
        });

        const usersAndGroupsProvider = new PeoplePickerProvider(this.pageContext, {
            identityTypes: ["user", "group"],
            options: {
                MinResults: 5,
                MaxResults: 20
            },
            operationScopes: ["ims"]
        });

        const teamsService = this.pageContext.getService<ITfsTeamService>("ITfsTeamService");
        const teams = query.myTeamsAsReviewer ? await teamsService.getMyTeams() : [];

        const errorMessage = new ObservableValue("");
        const showOverlay = new ObservableValue(false);

        const props: ICustomizeQueryPanelProps = {
            errorMessage,
            initialQuery: query,
            myTeamIds: teams.map(t => t.id),
            onDismiss: () => {
                onDismiss && onDismiss(false);
            },
            onSave: async query => {
                showOverlay.value = true;
                try {
                    await saveQuery(query);
                } catch (ex) {
                    errorMessage.value = ex.message;
                    showOverlay.value = false;
                    return;
                }
                onDismiss && onDismiss(true);
            },
            showOverlay,
            usersAndGroupsProvider,
            usersOnlyProvider
        };

        return props;
    }

    private onDismiss = (panelDismiss?: () => void) => {
        if (this.isChanged) {
            this.isChanged = false;
            const prDataService = this.pageContext.getService<IPullRequestDataProviderService>("IPullRequestDataProviderService");
            prDataService.getPullRequestSections(true);
        }

        panelDismiss && panelDismiss();
    };

    private saveQueries(queries: IPullRequestQuery[], resetResults: boolean): Promise<void> {
        const serializedCriteria = queries.length > 0 ? JSON.stringify(queries) : "";
        const preferences = { pullRequestListCustomCriteria: serializedCriteria } as IVersionControlUserPreferences;

        const vcViewModelService = this.pageContext.getService<IVCViewModelDataProviderService>("IVCViewModelDataProviderService");
        return vcViewModelService.setUserPreferences(preferences).then(() => {
            if (resetResults) {
                this.isChanged = true;
            }
        });
    }
}

Services.add("IPullRequestListingPageActionsService", { serviceFactory: PullRequestListingPageActionsService });
